import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Contract, formatUnits, formatEther, ZeroAddress } from 'ethers';
import type { JsonRpcSigner, BrowserProvider } from 'ethers';
import styles from './SwapContainer.module.css';
import { TokenInput, type TokenMeta } from '../components/TokenInput';
import { SlippageSettings } from '../components/SlippageSettings';
import { TxModal, type TxStatus } from '../components/TxModal';
import {
  ROUTER_ABI, V3_FACTORY_ABI, CPMM_FACTORY_ABI, STABLE_FACTORY_ABI,
  CPMM_POOL_ABI, STABLE_POOL_ABI, V3_POOL_ABI,
} from '../constants/abis';
import { getAddresses, V3_FEE_TIERS } from '../constants/addresses';
import { useMergedTokenList } from '../hooks/useMergedTokenList';
import { batchBalanceAndAllowance } from '../utils/erc20Multicall';
import {
  quoteExactInputSingleStatic,
  quoteExactOutputSingleStaticLoose,
  estimateSwapGas,
} from '../utils/routerQuote';
import {
  parseTokenInput, formatTokenAmount, humanizeError,
  applySlippageTolerance, applySlippageToleranceMax, getDeadline,
} from '../utils/formatting';
import { approveToken } from '../hooks/useTokenBalance';
import { useSettingsStore } from '../state';
import { BRAND } from '../constants/brandAssets';

interface SwapContainerProps {
  signer: JsonRpcSigner | null;
  provider: BrowserProvider | null;
  address: string | null;
  chainId: number | null;
}

type AmmKind = 'auto' | 'cpmm';
type Direction = 'exactIn' | 'exactOut';

export function SwapContainer({ signer, provider, address, chainId }: SwapContainerProps) {
  const addrs = chainId ? getAddresses(chainId) : null;
  const { tokens: knownTokens } = useMergedTokenList(chainId);

  const [tokenIn, setTokenIn] = useState<TokenMeta | null>(null);
  const [tokenOut, setTokenOut] = useState<TokenMeta | null>(null);
  const [amountIn, setAmountIn] = useState('');
  const [amountOut, setAmountOut] = useState('');
  const [direction, setDirection] = useState<Direction>('exactIn');
  const [ammKind, setAmmKind] = useState<AmmKind>('auto');
  const [v3Fee, setV3Fee] = useState(3000);

  // Quote state
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  /** Non-fatal hints (approximate quote, connect wallet) — distinct from hard errors */
  const [quoteInfo, setQuoteInfo] = useState('');
  const [detectedAmm, setDetectedAmm] = useState('');
  const [priceImpact, setPriceImpact] = useState<number | null>(null);
  const [quoteExact, setQuoteExact] = useState(false);
  const [gasDisplay, setGasDisplay] = useState<string | null>(null);

  // Balances
  const [balIn, setBalIn] = useState<bigint>(0n);
  const [balOut, setBalOut] = useState<bigint>(0n);
  const [allowanceIn, setAllowanceIn] = useState<bigint>(0n);

  // Settings (persisted)
  const slippageBps = useSettingsStore(s => s.slippageBps);
  const deadlineMinutes = useSettingsStore(s => s.deadlineMinutes);
  const setSlippageBps = useSettingsStore(s => s.setSlippageBps);
  const setDeadlineMinutes = useSettingsStore(s => s.setDeadlineMinutes);

  // TX state
  const [txStatus, setTxStatus] = useState<TxStatus>('idle');
  const [txHash, setTxHash] = useState<string>();
  const [txError, setTxError] = useState<string>();

  const quoteTimer = useRef<ReturnType<typeof setTimeout>>();
  const gasTimer = useRef<ReturnType<typeof setTimeout>>();

  // â”€â”€ Fetch user balances â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const refreshBalances = useCallback(async () => {
    if (!provider || !address || !addrs) return;
    const { balIn, balOut, allowanceIn } = await batchBalanceAndAllowance(
      provider,
      address,
      addrs.router,
      tokenIn?.address ?? null,
      tokenOut?.address ?? null,
    );
    setBalIn(balIn);
    setBalOut(balOut);
    setAllowanceIn(allowanceIn);
  }, [provider, address, addrs, tokenIn, tokenOut]);

  useEffect(() => { refreshBalances(); }, [refreshBalances]);

  // â”€â”€ Quote â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const runQuote = useCallback(async () => {
    if (!provider || !tokenIn || !tokenOut || !addrs) return;
    const val = direction === 'exactIn' ? amountIn : amountOut;
    if (!val || val === '0') {
      direction === 'exactIn' ? setAmountOut('') : setAmountIn('');
      setDetectedAmm('');
      setQuoteInfo('');
      setQuoteError('');
      return;
    }

    const parsed = parseTokenInput(val, direction === 'exactIn' ? tokenIn.decimals : tokenOut.decimals);
    if (!parsed || parsed === 0n) return;

    setQuoteLoading(true);
    setQuoteError('');
    setQuoteInfo('');
    setDetectedAmm('');
    setQuoteExact(false);

    try {
      // Detect which AMM has a pool
      if (ammKind === 'cpmm') {
        const cpmmFactory = new Contract(addrs.cpmmFactory, CPMM_FACTORY_ABI, provider);
        const pool = await cpmmFactory.getPool(tokenIn.address, tokenOut.address, v3Fee) as string;
        if (pool === ZeroAddress) throw new Error(`No CPMM pool for fee ${v3Fee}`);
        const poolC = new Contract(pool, CPMM_POOL_ABI, provider);
        setQuoteExact(true);
        if (direction === 'exactIn') {
          const out = await poolC.getAmountOut(parsed, tokenIn.address) as bigint;
          setAmountOut(formatUnits(out, tokenOut.decimals));
          setDetectedAmm('CPMM (x·y=k)');
        } else {
          const inAmt = await poolC.getAmountIn(parsed, tokenIn.address) as bigint;
          setAmountIn(formatUnits(inAmt, tokenIn.decimals));
          setDetectedAmm('CPMM (x·y=k)');
        }
        return;
      }

      // Auto: try stable first (fee=0 path), then V3
      const stableFactory = new Contract(addrs.stableFactory, STABLE_FACTORY_ABI, provider);
      const stablePool = await stableFactory.getPool(tokenIn.address, tokenOut.address) as string;

      if (stablePool !== ZeroAddress) {
        const sp = new Contract(stablePool, STABLE_POOL_ABI, provider);
        const t0 = await sp.token0() as string;
        const i = t0.toLowerCase() === tokenIn.address.toLowerCase() ? 0 : 1;
        const j = 1 - i;
        setQuoteExact(true);
        if (direction === 'exactIn') {
          const out = await sp.getDy(i, j, parsed) as bigint;
          setAmountOut(formatUnits(out, tokenOut.decimals));
          setDetectedAmm('StableSwap (Curve-style)');
        } else {
          const inAmt = await sp.getDx(i, j, parsed) as bigint;
          setAmountIn(formatUnits(inAmt, tokenIn.decimals));
          setDetectedAmm('StableSwap (Curve-style)');
        }
        return;
      }

      // V3 path — prefer router eth_call when wallet has approved (router pulls from msg.sender)
      const v3Factory = new Contract(addrs.v3Factory, V3_FACTORY_ABI, provider);
      const v3Pool = await v3Factory.getPool(tokenIn.address, tokenOut.address, v3Fee) as string;
      if (v3Pool === ZeroAddress) throw new Error('No pool found. Try a different fee tier or AMM type.');
      setDetectedAmm(`CLMM (${(v3Fee / 10000).toFixed(2)}%)`);

      const deadline = BigInt(getDeadline(deadlineMinutes));
      const recipient = address ?? ZeroAddress;

      if (address) {
        if (direction === 'exactIn' && allowanceIn >= parsed) {
          const out = await quoteExactInputSingleStatic(provider, addrs.router, address, {
            tokenIn: tokenIn.address,
            tokenOut: tokenOut.address,
            fee: v3Fee,
            recipient,
            deadline,
            amountIn: parsed,
            amountOutMinimum: 0n,
            sqrtPriceLimitX96: 0n,
            ammKind: 0,
          });
          if (out != null) {
            setAmountOut(formatUnits(out, tokenOut.decimals));
            setQuoteExact(true);
            setQuoteError('');
            return;
          }
        }
        if (direction === 'exactOut') {
          const amtIn = await quoteExactOutputSingleStaticLoose(provider, addrs.router, address, {
            tokenIn: tokenIn.address,
            tokenOut: tokenOut.address,
            fee: v3Fee,
            recipient,
            deadline,
            amountOut: parsed,
            sqrtPriceLimitX96: 0n,
            ammKind: 0,
          });
          if (amtIn != null) {
            setAmountIn(formatUnits(amtIn, tokenIn.decimals));
            setQuoteExact(true);
            setQuoteError('');
            return;
          }
        }
      }

      setQuoteExact(false);
      setQuoteInfo(
        address
          ? `Approximate rate from spot price — approve ${tokenIn.symbol} for an exact router quote.`
          : 'Connect wallet for exact on-chain quotes.',
      );
      const poolC = new Contract(v3Pool, V3_POOL_ABI, provider);
      const [sqrtPriceX96] = await poolC.slot0() as [bigint, ...unknown[]];
      const sqrtPriceNum = Number(sqrtPriceX96) / Number(2n ** 96n);
      const price = sqrtPriceNum * sqrtPriceNum;

      const t0Addr = await poolC.token0() as string;
      const isToken0In = t0Addr.toLowerCase() === tokenIn.address.toLowerCase();
      const effectivePrice = isToken0In ? price : 1 / price;

      if (direction === 'exactIn') {
        const decAdj = 10 ** (tokenIn.decimals - tokenOut.decimals);
        const rawOut = Number(formatUnits(parsed, tokenIn.decimals)) * effectivePrice / decAdj;
        setAmountOut(rawOut.toFixed(tokenOut.decimals > 6 ? 8 : 6));
      } else {
        const decAdj = 10 ** (tokenOut.decimals - tokenIn.decimals);
        const rawIn = Number(formatUnits(parsed, tokenOut.decimals)) / effectivePrice * decAdj;
        setAmountIn(rawIn.toFixed(tokenIn.decimals > 6 ? 8 : 6));
      }
    } catch (err) {
      setQuoteError(humanizeError(err));
    } finally {
      setQuoteLoading(false);
    }
  }, [provider, tokenIn, tokenOut, amountIn, amountOut, direction, ammKind, v3Fee, addrs,
      address, allowanceIn, deadlineMinutes]);

  // Debounce quote
  useEffect(() => {
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(runQuote, 400);
    return () => clearTimeout(quoteTimer.current);
  }, [runQuote]);

  const runGasEstimate = useCallback(async () => {
    if (!signer || !provider || !tokenIn || !tokenOut || !addrs || !address) {
      setGasDisplay(null);
      return;
    }
    const inAmt = parseTokenInput(amountIn, tokenIn.decimals);
    const outAmt = parseTokenInput(amountOut, tokenOut.decimals);
    if (!inAmt || !outAmt) {
      setGasDisplay(null);
      return;
    }
    const recipient = address;
    const deadline = BigInt(getDeadline(deadlineMinutes));
    const minOut = applySlippageTolerance(outAmt, slippageBps);
    const maxIn = applySlippageToleranceMax(inAmt, slippageBps);

    let feeForRouter = v3Fee;
    if (ammKind !== 'cpmm') {
      const stableFactory = new Contract(addrs.stableFactory, STABLE_FACTORY_ABI, provider);
      const stablePool = await stableFactory.getPool(tokenIn.address, tokenOut.address) as string;
      feeForRouter = stablePool !== ZeroAddress ? 0 : v3Fee;
    }

    let gas: bigint | null = null;

    if (ammKind === 'cpmm') {
      if (direction === 'exactIn') {
        gas = await estimateSwapGas(signer, addrs.router, {
          tokenIn: tokenIn.address,
          tokenOut: tokenOut.address,
          fee: v3Fee,
          recipient,
          deadline,
          amountIn: inAmt,
          amountOutMinimum: minOut,
          sqrtPriceLimitX96: 0n,
          ammKind: 2,
        }, 'in');
      } else {
        gas = await estimateSwapGas(signer, addrs.router, {
          tokenIn: tokenIn.address,
          tokenOut: tokenOut.address,
          fee: v3Fee,
          recipient,
          deadline,
          amountOut: outAmt,
          amountInMaximum: maxIn,
          sqrtPriceLimitX96: 0n,
          ammKind: 2,
        }, 'out');
      }
    } else if (direction === 'exactIn') {
      gas = await estimateSwapGas(signer, addrs.router, {
        tokenIn: tokenIn.address,
        tokenOut: tokenOut.address,
        fee: feeForRouter,
        recipient,
        deadline,
        amountIn: inAmt,
        amountOutMinimum: minOut,
        sqrtPriceLimitX96: 0n,
        ammKind: 0,
      }, 'in');
    } else {
      gas = await estimateSwapGas(signer, addrs.router, {
        tokenIn: tokenIn.address,
        tokenOut: tokenOut.address,
        fee: feeForRouter,
        recipient,
        deadline,
        amountOut: outAmt,
        amountInMaximum: maxIn,
        sqrtPriceLimitX96: 0n,
        ammKind: 0,
      }, 'out');
    }

    if (gas == null) {
      setGasDisplay(null);
      return;
    }
    const fd = await provider.getFeeData();
    const price = fd.maxFeePerGas ?? fd.gasPrice ?? 0n;
    const wei = gas * price;
    setGasDisplay(`~${gas.toLocaleString()} gas · ~${formatEther(wei).slice(0, 10)} ETH`);
  }, [signer, provider, tokenIn, tokenOut, addrs, address, amountIn, amountOut, direction, ammKind, v3Fee, slippageBps, deadlineMinutes]);

  useEffect(() => {
    clearTimeout(gasTimer.current);
    gasTimer.current = setTimeout(() => { void runGasEstimate(); }, 600);
    return () => clearTimeout(gasTimer.current);
  }, [runGasEstimate]);

  const executeSwap = useCallback(async () => {
    if (!signer || !tokenIn || !tokenOut || !addrs) return;

    const inAmt = parseTokenInput(amountIn, tokenIn.decimals);
    const outAmt = parseTokenInput(amountOut, tokenOut.decimals);
    if (!inAmt || !outAmt) return;

    const router = new Contract(addrs.router, ROUTER_ABI, signer);
    const recipient = await signer.getAddress();
    const deadline = BigInt(getDeadline(deadlineMinutes));

    // Check allowance
    if (allowanceIn < inAmt) {
      setTxStatus('approving');
      try {
        await approveToken(tokenIn.address, addrs.router, inAmt * 10n, signer); // approve 10× for fewer prompts
        await refreshBalances();
      } catch (err) {
        setTxError(humanizeError(err));
        setTxStatus('error');
        return;
      }
    }

    setTxStatus('pending');
    setTxError(undefined);
    setTxHash(undefined);

    try {
      const minOut = applySlippageTolerance(outAmt, slippageBps);
      const maxIn = applySlippageToleranceMax(inAmt, slippageBps);

      let tx;

      if (ammKind === 'cpmm') {
        if (direction === 'exactIn') {
          tx = await router.exactInputSingle({
            tokenIn: tokenIn.address,
            tokenOut: tokenOut.address,
            fee: v3Fee,
            recipient,
            deadline,
            amountIn: inAmt,
            amountOutMinimum: minOut,
            sqrtPriceLimitX96: 0n,
            ammKind: 2,
          });
        } else {
          tx = await router.exactOutputSingle({
            tokenIn: tokenIn.address,
            tokenOut: tokenOut.address,
            fee: v3Fee,
            recipient,
            deadline,
            amountOut: outAmt,
            amountInMaximum: maxIn,
            sqrtPriceLimitX96: 0n,
            ammKind: 2,
          });
        }
      } else {
        // AUTO: router will detect stable (fee=0) or V3
        const feeForRouter = detectedAmm.includes('Stable') ? 0 : v3Fee;
        if (direction === 'exactIn') {
          tx = await router.exactInputSingle({
            tokenIn: tokenIn.address,
            tokenOut: tokenOut.address,
            fee: feeForRouter,
            recipient,
            deadline,
            amountIn: inAmt,
            amountOutMinimum: minOut,
            sqrtPriceLimitX96: 0n,
            ammKind: 0,
          });
        } else {
          tx = await router.exactOutputSingle({
            tokenIn: tokenIn.address,
            tokenOut: tokenOut.address,
            fee: feeForRouter,
            recipient,
            deadline,
            amountOut: outAmt,
            amountInMaximum: maxIn,
            sqrtPriceLimitX96: 0n,
            ammKind: 0,
          });
        }
      }

      setTxHash(tx.hash);
      await tx.wait();
      setTxStatus('success');
      setAmountIn('');
      setAmountOut('');
      refreshBalances();
    } catch (err) {
      setTxError(humanizeError(err));
      setTxStatus('error');
    }
  }, [signer, tokenIn, tokenOut, addrs, amountIn, amountOut, direction, ammKind,
      v3Fee, detectedAmm, allowanceIn, slippageBps, deadlineMinutes, refreshBalances]);

  const flipTokens = () => {
    setTokenIn(tokenOut);
    setTokenOut(tokenIn);
    setAmountIn(amountOut);
    setAmountOut(amountIn);
    setDirection(d => d === 'exactIn' ? 'exactOut' : 'exactIn');
    setBalIn(balOut);
    setBalOut(balIn);
  };

  const parsedIn = tokenIn && amountIn ? parseTokenInput(amountIn, tokenIn.decimals) : null;
  const insufficientIn =
    !!signer &&
    !!tokenIn &&
    parsedIn != null &&
    parsedIn > 0n &&
    balIn < parsedIn;

  const txBusy = txStatus === 'approving' || txStatus === 'pending';

  const canSwap =
    !!signer &&
    !!tokenIn &&
    !!tokenOut &&
    !!amountIn &&
    !!amountOut &&
    !quoteLoading &&
    !insufficientIn &&
    !quoteError &&
    !txBusy;

  const needsApproval = !!tokenIn && !!amountIn && (() => {
    const p = parseTokenInput(amountIn, tokenIn.decimals);
    return p ? allowanceIn < p : false;
  })();

  const swapLabel = (() => {
    if (txBusy) return 'Confirm in wallet…';
    if (quoteLoading) return 'Fetching quote…';
    if (insufficientIn) return `Insufficient ${tokenIn?.symbol ?? ''} balance`;
    if (quoteError) return 'Fix quote error above';
    if (needsApproval) return `Approve ${tokenIn?.symbol ?? 'token'}`;
    return 'Swap';
  })();

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.cardHeader}>
          <div className={styles.swapTitleRow}>
            <img
              src={BRAND.headerMarkNextToWallet}
              alt=""
              className={styles.swapTitleLogo}
              width={36}
              height={36}
              decoding="async"
            />
            <h2 className={styles.cardTitle}>Swap</h2>
          </div>
          <SlippageSettings
            slippageBps={slippageBps}
            deadlineMinutes={deadlineMinutes}
            onSlippageChange={setSlippageBps}
            onDeadlineChange={setDeadlineMinutes}
          />
        </div>

        {/* AMM selector */}
        <div className={styles.ammRow}>
          <span className={styles.ammLabel}>AMM</span>
          <div className={styles.ammTabs}>
            {(['auto', 'cpmm'] as AmmKind[]).map(k => (
              <button
                key={k}
                className={`${styles.ammTab} ${ammKind === k ? styles.ammTabActive : ''}`}
                onClick={() => setAmmKind(k)}
                type="button"
              >
                {k === 'auto' ? 'Auto (CLMM / Stable)' : 'CPMM'}
              </button>
            ))}
          </div>
          {/* Fee tier selector */}
          <select
            className={styles.feeSelect}
            value={v3Fee}
            onChange={e => setV3Fee(Number(e.target.value))}
          >
            {V3_FEE_TIERS.map(f => (
              <option key={f.fee} value={f.fee}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Token In */}
        <TokenInput
          label="You Pay"
          value={amountIn}
          onChange={v => { setAmountIn(v); setDirection('exactIn'); }}
          token={tokenIn}
          onTokenChange={t => { setTokenIn(t); }}
          balance={balIn}
          provider={provider}
          userAddress={address}
          showMax
          suggestedTokens={knownTokens}
        />

        {/* Flip Button */}
        <div className={styles.flipRow}>
          <button className={styles.flipBtn} onClick={flipTokens} type="button" title="Flip tokens">
            <FlipIcon />
          </button>
          {detectedAmm && (
            <span className={styles.detectedAmm}>{detectedAmm}</span>
          )}
        </div>

        {/* Token Out */}
        <TokenInput
          label="You Receive"
          value={amountOut}
          onChange={v => { setAmountOut(v); setDirection('exactOut'); }}
          token={tokenOut}
          onTokenChange={t => { setTokenOut(t); }}
          balance={balOut}
          provider={provider}
          userAddress={address}
          readOnly={quoteLoading}
          suggestedTokens={knownTokens}
        />

        {insufficientIn && tokenIn && (
          <div className={styles.quoteError} role="alert">
            Amount exceeds your {tokenIn.symbol} balance ({formatTokenAmount(balIn, tokenIn.decimals)} available).
          </div>
        )}

        {quoteInfo && !quoteError && (
          <div className={styles.quoteInfo} role="status">
            {quoteInfo}
          </div>
        )}

        {quoteError && <div className={styles.quoteError}>{quoteError}</div>}

        {/* Price info row */}
        {tokenIn && tokenOut && amountIn && amountOut && !quoteLoading && (
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>Rate</span>
            <span className={styles.priceValue}>
              1 {tokenIn.symbol} ≈ {formatAdaptiveRate(amountIn, amountOut)} {tokenOut.symbol}
            </span>
          </div>
        )}

        {tokenIn && tokenOut && amountIn && amountOut && !quoteLoading && (
          <div className={styles.quoteMeta}>
            Quote: {quoteExact ? 'Exact (from pool or router)' : 'Estimate (from pool price)'}
          </div>
        )}

        {slippageBps > 500 && tokenIn && tokenOut && amountIn && amountOut && (
          <div className={styles.slippageWarn} role="status">
            High slippage ({(slippageBps / 100).toFixed(2)}%) — you may receive significantly less than quoted.
          </div>
        )}

        {/* Slippage warning */}
        {tokenIn && tokenOut && amountIn && amountOut && (
          <div className={styles.slippageRow}>
            <span className={styles.slippageLabel}>Min. received ({slippageBps / 100}% slippage)</span>
            <span className={styles.slippageValue}>
              {(() => {
                const p = parseTokenInput(amountOut, tokenOut?.decimals ?? 18);
                if (!p) return '–';
                const min = applySlippageTolerance(p, slippageBps);
                return `${formatTokenAmount(min, tokenOut?.decimals ?? 18)} ${tokenOut?.symbol ?? ''}`;
              })()}
            </span>
          </div>
        )}

        {gasDisplay && signer && tokenIn && tokenOut && amountIn && amountOut && !quoteLoading && (
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>Network fee (est.)</span>
            <span className={styles.priceValue}>{gasDisplay}</span>
          </div>
        )}

        {/* Swap button */}
        {!signer ? (
          <div className={styles.connectPrompt}>Connect wallet to swap</div>
        ) : (
          <button
            className={`${styles.swapBtn} ${!canSwap ? styles.swapBtnDisabled : ''}`}
            onClick={executeSwap}
            disabled={!canSwap}
            type="button"
          >
            {swapLabel}
          </button>
        )}

        {/* CLMM multi-hop note */}
        <div className={styles.hint}>
          Multi-hop routes (several pools in one transaction) may appear for some pairs. Always review amounts in your wallet before you confirm.
        </div>
      </div>

      <TxModal
        status={txStatus}
        txHash={txHash}
        errorMsg={txError}
        chainId={chainId ?? undefined}
        onClose={() => setTxStatus('idle')}
        title="Swap Pending…"
      />
    </div>
  );
}

function FlipIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3 6h12M3 6l3-3M3 6l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 12H3m12 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}



function formatAdaptiveRate(amountIn: string, amountOut: string): string {
  const inV = Number.parseFloat(amountIn)
  const outV = Number.parseFloat(amountOut)
  if (!Number.isFinite(inV) || !Number.isFinite(outV) || inV <= 0 || outV <= 0) return '–'

  const rate = outV / inV
  if (!Number.isFinite(rate) || rate <= 0) return '–'

  if (rate < 1e-6) return rate.toExponential(6)
  if (rate < 1e-3) return rate.toFixed(9)
  if (rate < 1) return rate.toFixed(6)
  if (rate < 1_000) {
    return rate.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    })
  }

  return rate.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

